import React from 'react';
import classNames from 'classnames';
import update from 'immutability-helper';
import { Spec } from 'immutability-helper';
import { connect } from 'react-redux';
import { DropTarget, ConnectDropTarget, DropTargetMonitor } from 'react-dnd';
import { flow, reject, findIndex, filter } from 'lodash';
import { sprintf } from 'sprintf';

import * as Types from 'types';

import { DRAGGABLE_TYPES } from 'constants/global';
import { friendlyMatcher } from 'services/friendlyMatcher';
import { newPlaylistArtwork, newPlaylist } from 'features/playlists/helpers';
import { setPlaylists, setCurrentPlaylist } from 'features/playlists/actions';
import { PlaylistArtworkContainer } from 'playlists/PlaylistArtworkContainer';
import { withPlayerDispatcher, withPlayerDispatcherProps } from 'player/withPlayerDispatcher';
import { PlaylistArtworkTrashContainer } from 'playlists/PlaylistArtworkTrashContainer';

const playlistDropTarget = {
  hover(props: Props, monitor: DropTargetMonitor, playlist: Playlist) {
    if (!props.droppable) {
      return;
    }

    if (monitor.isOver({ shallow: true })) {
      playlist.insertArtwork(monitor.getItem());
    }
  },

  drop(props: Props) {
    return props;
  },
};

type OwnProps = {
  /**
   * Playlist items to display.
   */
  items: Types.PlaylistArtwork[];
  /**
   * Displayed playlist ID. Can be undefined if playlist
   * is virtual eg. Art Stream selection artworks.
   *
   * This is to workaround issue when playing artworks from playlist.
   * When playlist is persisted we want to use it's ID, when virtual
   * we want to copy virtual playlist to state.
   */
  itemsPlaylistId?: string;
};

type StateProps = {
  isEdit: boolean;
  playlistIsEditable: boolean;
  /**
   * Playlist that is being currently manipulated.
   */
  currentPlaylist: Types.Playlist;
  filterQuery?: string;
  showAdd: boolean;
  showRemove: boolean;
  droppable: boolean;
} & Types.Styleable;

type DispatchProps = {
  setPlaylists: (playlists: Types.Playlist[], playerOptions?: Types.PlayerOptions) => void;
  setCurrentPlaylist: (playlist: Types.Playlist) => void;
};

type HOCProps = {
  playlistConnectDropTarget: ConnectDropTarget;
} & withPlayerDispatcherProps;

type Props = StateProps & DispatchProps & HOCProps & OwnProps;

type State = {
  flashNoticeById: Record<string, string>;
  flashNoticeTargetId?: number;
  flashNotice?: string;
};

class Playlist extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      flashNoticeById: {},
      flashNoticeTargetId: undefined,
      flashNotice: undefined,
    };

    this.moveArtwork = this.moveArtwork.bind(this);
    this.insertArtwork = this.insertArtwork.bind(this);
    this.onEndDrag = this.onEndDrag.bind(this);
    this.onAddClick = this.onAddClick.bind(this);
    this.removeByPlaylistArtwork = this.removeByPlaylistArtwork.bind(this);
    this.onPlayButtonClick = this.onPlayButtonClick.bind(this);
  }

  onAddClick(playlistArtworkToAdd: Types.PlaylistArtwork) {
    const playlistArtwork = newPlaylistArtwork(playlistArtworkToAdd.artwork);
    const addedAt = this.insertArtwork(playlistArtwork);

    if (addedAt && !Number.isNaN(addedAt)) {
      const { flashNoticeById } = this.state;
      this.setState({
        flashNoticeById: {
          ...flashNoticeById,
          [playlistArtworkToAdd.id]: `Artwork added at position #${addedAt + 1}`,
        },
      });

      setTimeout(() => {
        const flashNoticeById = {
          ...this.state.flashNoticeById,
        };
        delete flashNoticeById[playlistArtworkToAdd.id];
        this.setState({ flashNoticeById });
      }, 1000);
    }
  }

  onPlayButtonClick(playlistArtwork: Types.PlaylistArtwork) {
    const { itemsPlaylistId } = this.props;

    if (itemsPlaylistId) {
      this.props.play(itemsPlaylistId, playlistArtwork.id);
    } else {
      // Playlist not persisted, let's copy and play
      const playerPlaylist = newPlaylist({
        items: this.props.items,
      });
      this.props.setPlaylists([playerPlaylist]);
      this.props.play(playerPlaylist.id, playlistArtwork.id);
    }
  }

  onEndDrag(_props: Props, monitor: DropTargetMonitor) {
    const dropResult = monitor.getDropResult();
    const { currentPlaylist } = this.props;

    // ended dragging on an item which is droppable,
    if (dropResult && dropResult.droppable) {
      currentPlaylist.items.forEach((playlistArtwork, index) => {
        // remove placeholder mark from newly inserted artworks
        playlistArtwork.placeholder = undefined;
        // Set position, React DnD orders within array
        playlistArtwork.position = currentPlaylist.items.length - index;
      });
      // ended dragging NOT on playlist, remove newly inserted artwork
    } else {
      const items = reject(
        currentPlaylist.items,
        (playlistArtwork) => playlistArtwork.placeholder !== undefined,
      );

      const updatedPlaylist = update(currentPlaylist, {
        items: {
          $set: items,
        },
      });

      this.props.setCurrentPlaylist(updatedPlaylist);
    }
  }

  insertArtwork(playlistArtwork: Types.PlaylistArtwork, explicitInsertIndex?: number) {
    const { currentPlaylist } = this.props;
    if (!currentPlaylist) {
      return false;
    }

    const foundIndex: number | undefined = findIndex(currentPlaylist.items, {
      id: playlistArtwork.id,
    });
    let arrayMutation: Spec<any[]>;

    let insertAtIndex: number | undefined = explicitInsertIndex;

    if (foundIndex !== -1) {
      if (insertAtIndex === undefined) {
        insertAtIndex = currentPlaylist.items.length - 1;
      }

      if (foundIndex === insertAtIndex) {
        return false;
      }
      // Move existing element.
      arrayMutation = {
        $splice: [
          [foundIndex, 1],
          [insertAtIndex, 0, { ...playlistArtwork }],
        ],
      };
    } else {
      if (insertAtIndex === undefined) {
        insertAtIndex = currentPlaylist.items.length;
      }
      arrayMutation = {
        $splice: [[insertAtIndex, 0, { ...playlistArtwork }]],
      };
    }

    const updatedPlaylist = update(currentPlaylist, {
      items: arrayMutation,
    });
    this.props.setCurrentPlaylist(updatedPlaylist);
    return insertAtIndex;
  }

  moveArtwork(dragIndex: number, hoverIndex: number) {
    const { currentPlaylist } = this.props;
    if (!currentPlaylist === undefined) {
      return;
    }

    const playlistArtwork = currentPlaylist.items[dragIndex];
    if (!playlistArtwork) {
      return;
    }

    const updatedPlaylist = update(currentPlaylist, {
      items: {
        $splice: [
          [dragIndex, 1],
          [hoverIndex, 0, playlistArtwork],
        ],
      },
    });

    this.props.setCurrentPlaylist(updatedPlaylist);
  }

  removeByPlaylistArtwork(playlistArtworkToRemove: Types.PlaylistArtwork) {
    const { currentPlaylist } = this.props;

    const newPlaylistObject = {
      ...currentPlaylist,
      items: reject(
        currentPlaylist.items,
        (playlistArtwork) => playlistArtwork.id === playlistArtworkToRemove.id,
      ),
    };

    this.props.setCurrentPlaylist(newPlaylistObject);
  }

  render() {
    const { isEdit, playlistIsEditable } = this.props;
    let { items, filterQuery, style } = this.props;

    const className = classNames('vault-playlist', this.props.className);

    if (filterQuery) {
      items = filter(items, ({ artwork }) =>
        friendlyMatcher(filterQuery, artwork.title, artwork.artistName),
      );
    }

    return this.props.playlistConnectDropTarget(
      <div className={className} style={style}>
        <div className='vault-playlist__list pos-rel'>
          {items.map((playlistArtwork, index) => (
            <PlaylistArtworkContainer
              key={playlistArtwork.id}
              index={index}
              playlistArtwork={playlistArtwork}
              moveArtwork={this.moveArtwork}
              insertArtwork={this.insertArtwork}
              onEndDrag={this.onEndDrag}
              droppable={this.props.droppable}
              onPlayButtonClick={this.onPlayButtonClick}
              onAddClick={this.onAddClick}
              showAdd={this.props.showAdd}
              onRemoveClick={this.props.showRemove && this.removeByPlaylistArtwork}
              canDrag={isEdit}
              flashNotice={this.state.flashNoticeById[playlistArtwork.id]}
            />
          ))}
          {filterQuery && items.length == 0 && (
            <div className='key-value--color-subtle text-center'>
              {sprintf('No artworks matched ‘%s’ query.', filterQuery)}
            </div>
          )}
          {isEdit && playlistIsEditable && <div className='vault-playlist-placeholder' />}
          <PlaylistArtworkTrashContainer
            isVisible={playlistIsEditable}
            onDrop={this.removeByPlaylistArtwork}
          />
        </div>
      </div>,
    );
  }
}

function dropSourceType(props: Props) {
  return props.droppable ? DRAGGABLE_TYPES.PLAYLIST_ARTWORK : 'none';
}

const mapStateToProps = ({ playlists }: Types.RootState) => ({
  currentPlaylist: playlists.current,
  playlistsById: playlists.byId,
});

const mapDispatchToProps = {
  setCurrentPlaylist,
  setPlaylists,
};

export const PlaylistContainer = flow(
  DropTarget(dropSourceType, playlistDropTarget, (dndConnect) => ({
    playlistConnectDropTarget: dndConnect.dropTarget(),
  })),
  connect(mapStateToProps, mapDispatchToProps),
  withPlayerDispatcher,
)(Playlist);
